import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

import { CartQuotationFacade } from '@/app/features/shared/quotation/facades/cart-quotation.facade';
import {
  QUOTATION_PROGRESS_STEPS,
  QUOTATION_STEP_LABELS,
} from '@/app/features/shared/quotation/interfaces/quotation-progress.models';

interface QuotationToastViewModel {
  id: string;
  productName: string;
  currentStepLabel: string;
  progressPercent: number;
}

/** Widget flotante global: mientras una cotización corre en segundo plano (hoy solo
 *  el flujo de Google Shopping, que no navega a /cart), avisa que sigue en curso sin
 *  bloquear al usuario. Se oculta en /cart porque ahí cada producto ya muestra su
 *  propia card de progreso (ver cart.page.html), y duplicarlo sería ruido. */
@Component({
  selector: 'app-quotation-progress-toast',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './quotation-progress-toast.html',
  styleUrl: './quotation-progress-toast.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotationProgressToastComponent {
  private readonly cartQuotationFacade = inject(CartQuotationFacade);
  private readonly router = inject(Router);

  private readonly onCartPage = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.startsWith('/cart')),
      startWith(this.router.url.startsWith('/cart'))
    ),
    { initialValue: this.router.url.startsWith('/cart') }
  );

  readonly toasts = computed<QuotationToastViewModel[]>(() => {
    if (this.onCartPage()) return [];

    return Array.from(this.cartQuotationFacade.activeQuotations().values()).map((q) => {
      const activeIndex = Math.min(q.completedSteps.length, QUOTATION_PROGRESS_STEPS.length - 1);

      return {
        id: q.id,
        productName: q.partial.name ?? 'tu producto',
        currentStepLabel: QUOTATION_STEP_LABELS[QUOTATION_PROGRESS_STEPS[activeIndex]],
        progressPercent: (q.completedSteps.length / QUOTATION_PROGRESS_STEPS.length) * 100,
      };
    });
  });
}
